import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'

import type { AudienceBase, AudienceFilterInput, BriefInput, Catalog } from '../lib/api'
import { api } from '../lib/api'
import { KPI_LABELS } from '../lib/labels'

/** Teto de territórios que a descoberta entrega ao enquadramento. A estimativa de
 * cubos é teto pelo mesmo motivo de `scripts/matrix_run.py`: o enquadramento ainda
 * derruba os não-ativáveis. */
const MAX_TERRITORIES = 60

/** Espera o usuário parar de marcar antes de contar a base: cada combinação nova é
 * uma consulta ao Crunch. */
const BASE_DEBOUNCE_MS = 800

interface Props {
  onCreated: (analysisId: string) => void
}

export function BriefForm({ onCreated }: Props) {
  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [label, setLabel] = useState('')
  const [audienceLabel, setAudienceLabel] = useState('')
  const [selection, setSelection] = useState<Record<string, string[]>>({})
  const [categoryId, setCategoryId] = useState('')
  const [focus, setFocus] = useState('')
  const [competitors, setCompetitors] = useState<string[]>([])
  const [kpi, setKpi] = useState('')
  const [progress, setProgress] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [base, setBase] = useState<AudienceBase | null>(null)
  const [baseError, setBaseError] = useState<string | null>(null)

  useEffect(() => {
    api
      .catalog()
      .then(setCatalog)
      .catch((exc: Error) => setError(exc.message))
  }, [])

  const category = catalog?.categories.find((c) => c.id === categoryId)
  const autoAudience = useMemo(() => {
    if (!catalog) return ''
    const parts = catalog.audience_dimensions
      .filter((d) => selection[d.var]?.length)
      .map((d) =>
        d.options
          .filter((o) => selection[d.var].includes(o.value))
          .map((o) => o.label)
          .join(', '),
      )
    return parts.length ? parts.join(' · ') : 'População total'
  }, [catalog, selection])

  const filters = useMemo(
    () =>
      (catalog?.audience_dimensions ?? [])
        .filter((d) => selection[d.var]?.length)
        .flatMap((d): AudienceFilterInput[] =>
          d.array
            ? selection[d.var].map((subvar) => ({ var: d.var, op: 'selected', subvar, label: d.label }))
            : [{ var: d.var, op: 'in', values: selection[d.var], label: d.label }],
        ),
    [catalog, selection],
  )

  useEffect(() => {
    setBase(null)
    setBaseError(null)
    if (!catalog || filters.length === 0) return
    let stale = false
    const timer = setTimeout(() => {
      api
        .audienceBase({ market: catalog.market, label: autoAudience, filters })
        .then((b) => !stale && setBase(b))
        .catch((exc: Error) => !stale && setBaseError(exc.message))
    }, BASE_DEBOUNCE_MS)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [catalog, filters, autoAudience])

  if (!catalog) {
    return error ? <p className="brief-error">{error}</p> : <p className="brief-hint">Carregando catálogo…</p>
  }

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  const running = progress !== null && !error
  // Sem recorte a audiência é a própria base e toda afinidade dá 100: a matriz sai
  // plana. Só se admite enquanto o catálogo de audiência não existe (dev, fixture).
  const hasAudience =
    catalog.audience_dimensions.length === 0 || Object.values(selection).some((v) => v.length)
  // Recorte vazio só se descobre depois da varredura inteira — por isso o envio espera
  // a contagem. Sem catálogo de audiência não há recorte para contar.
  const baseOk =
    catalog.audience_dimensions.length === 0 ||
    (base !== null && (base.unweighted === null || base.unweighted >= base.minimum))
  const ready =
    Boolean(focus && kpi && category && !category.refused_reason) &&
    hasAudience &&
    baseOk &&
    !running

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!catalog || !category) return
    const brief: BriefInput = {
      label: label.trim() || null,
      audience: {
        market: catalog.market,
        label: audienceLabel.trim() || autoAudience,
        filters,
      },
      brands: { focus, competitors, category_id: category.id },
      objective: { kpi },
    }
    setError(null)
    setProgress([])
    try {
      const result = await api.createAnalysis(brief, (_stage, message) =>
        setProgress((current) => [...(current ?? []), message]),
      )
      onCreated(result.analysis_id)
    } catch (exc) {
      setError((exc as Error).message)
    }
  }

  return (
    <form className="brief-form" onSubmit={submit}>
      <fieldset disabled={running}>
        <section className="brief-step">
          <div className="section-label"><span>01</span><p>Audiência</p></div>
          {catalog.audience_dimensions.length === 0 && (
            <p className="brief-hint">
              O catálogo de audiência ainda não foi gerado — a análise roda sobre a população
              total, e sem recorte toda afinidade dá 100. Serve para testar o fluxo, não para
              decidir.
            </p>
          )}
          <div className="brief-dimensions">
            {catalog.audience_dimensions.map((dimension) => (
              <div className="brief-dimension" key={dimension.var}>
                <p className="eyebrow">{dimension.label}</p>
                {dimension.options.map((option) => (
                  <label className="brief-check" key={option.value}>
                    <input
                      type="checkbox"
                      checked={selection[dimension.var]?.includes(option.value) ?? false}
                      onChange={() =>
                        setSelection({
                          ...selection,
                          [dimension.var]: toggle(selection[dimension.var] ?? [], option.value),
                        })
                      }
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            ))}
          </div>
          <label className="brief-field">
            <span>Nome da audiência</span>
            <input value={audienceLabel} placeholder={autoAudience} onChange={(e) => setAudienceLabel(e.target.value)} />
          </label>
          {filters.length > 0 && (
            <p className="brief-hint" aria-live="polite">
              {baseError
                ? `Não foi possível contar a base: ${baseError}`
                : base === null || base.unweighted === null
                  ? 'Contando respondentes…'
                  : base.unweighted < base.minimum
                    ? `${base.unweighted.toLocaleString('pt-BR')} respondentes — abaixo do mínimo de ${base.minimum}. Desmarque eixos ou marque mais opções.`
                    : base.unweighted < base.recommended
                      ? `${base.unweighted.toLocaleString('pt-BR')} respondentes — roda, mas recortes estreitos assim costumam render poucos territórios.`
                      : `${base.unweighted.toLocaleString('pt-BR')} respondentes.`}
            </p>
          )}
        </section>

        <section className="brief-step">
          <div className="section-label"><span>02</span><p>Marca</p></div>
          <label className="brief-field">
            <span>Categoria</span>
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value)
                setFocus('')
                setCompetitors([])
              }}
            >
              <option value="">Escolha a categoria</option>
              {catalog.categories.map((c) => (
                <option key={c.id} value={c.id} disabled={Boolean(c.refused_reason)}>
                  {c.label}{c.refused_reason ? ' — base insuficiente' : ''}
                </option>
              ))}
            </select>
          </label>
          {category && (
            <>
              <label className="brief-field">
                <span>Marca foco</span>
                <select
                  value={focus}
                  onChange={(e) => {
                    setFocus(e.target.value)
                    setCompetitors(competitors.filter((c) => c !== e.target.value))
                  }}
                >
                  <option value="">Escolha a marca</option>
                  {category.brands.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
              </label>
              <p className="eyebrow">Concorrentes</p>
              <div className="brief-competitors">
                {category.brands
                  .filter((b) => b.id !== focus)
                  .map((b) => (
                    <label className="brief-check" key={b.id}>
                      <input
                        type="checkbox"
                        checked={competitors.includes(b.id)}
                        onChange={() => setCompetitors(toggle(competitors, b.id))}
                      />
                      {b.label}
                    </label>
                  ))}
              </div>
            </>
          )}
        </section>

        <section className="brief-step">
          <div className="section-label"><span>03</span><p>Objetivo</p></div>
          <label className="brief-field">
            <span>KPI</span>
            <select value={kpi} onChange={(e) => setKpi(e.target.value)}>
              <option value="">Escolha o KPI</option>
              {catalog.kpis.map((k) => <option key={k} value={k}>{KPI_LABELS[k] ?? k}</option>)}
            </select>
          </label>
          <label className="brief-field">
            <span>Nome da análise (opcional)</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} />
          </label>
        </section>

        <div className="brief-submit">
          <p className="brief-hint">
            Matriz: até {MAX_TERRITORIES * (2 + competitors.length)} consultas ao Crunch
            ({MAX_TERRITORIES} territórios × marca foco, {competitors.length} concorrente(s) e
            audiência). Uma audiência nunca analisada soma ~1.530 consultas na varredura e leva
            de 4 a 6 minutos; trocar marca ou KPI sobre a mesma audiência não repete a varredura.
          </p>
          <button type="submit" disabled={!ready}>Rodar análise</button>
        </div>
      </fieldset>

      {progress && (
        <ol className="brief-progress" aria-live="polite">
          {progress.map((message, index) => <li key={index}>{message}</li>)}
        </ol>
      )}
      {error && <p className="brief-error" role="alert">{error}</p>}
    </form>
  )
}
